import React, { useCallback, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { ExpandMore } from '@mui/icons-material'
import { Autocomplete, MenuItem, styled, TextField } from '@mui/material'
import block from 'bem-cn'

import { LocaleSlotsKeysType } from 'shared/locale/en-US'

import { selectLocaleDict } from 'features/locale/selectors'

import './AutoComplete.scss'

const b = block('auto-complete')

interface IOption {
  name: string
  value: string
}

interface IAutoCompleteProps {
  data: IOption[]
  onSelect: (provider: string) => void
  value: string
  defaultValue: string
}

const CustomMenuItem = styled(MenuItem)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'left',
  fontFamily: 'inherit !important',
  fontSize: 14,
  background: 'var(--Autocomplete-bg, #e8e8e8)',
  color: 'var(--Autocomplete-color, #8d8d8d)',
  // borderTop: '1px solid #111',
  fontWeight: 400,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textTransform: 'capitalize',
  '&:hover': {
    backgroundColor: '#e2effc !important',
    color: '#0f6eb8 !important',
  },
  '&.Mui-focused': {
    backgroundColor: '#e2effc !important',
    color: '#0f6eb8 !important',
  },
  '&.Mui-selected': {
    backgroundColor: '#e2effc !important',
    color: '#0f6eb8 !important',
  },
  '& .MuiAutocomplete-option': {
    backgroundColor: '#e2effc !important',
    color: '#0f6eb8 !important',
  },
})

const CustomAutoComplete = styled(Autocomplete)({
  background: 'var(--Autocomplete-bg, #e8e8e8)',
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
  height: '100%',
  position: 'relative',
  width: '100%',
  border: '1px solid var(--Autocomplete-border-color, #e0e0e0)',
  borderRadius: 'var(--Autocomplete-border-radius, 8px)',
  '& .MuiFormControl-root': {
    height: '100% !important',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiInputBase-root': {
    height: '100% !important',
    padding: '0 10px',
    background: 'transparent',
    color: '#202020',
    lineHeight: '100%',
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-root': {
    display: 'flex',
  },
  '& .MuiSvgIcon-root': {
    color: '#202020',
    width: '25px',
    height: '25px',
  },

  '& .MuiAutocomplete-clearIndicator': {
    display: 'none',
  },
})

export const AutoCompleteComponent: React.FC<IAutoCompleteProps> = props => {
  const { data, onSelect, value, defaultValue, ...otherProps } = props
  const locale = useSelector(selectLocaleDict, shallowEqual).slots

  const handleChangeSelect = useCallback(
    (newValue: IOption) => {
      if (!newValue) {
        onSelect(defaultValue)
        return
      }
      onSelect(newValue.value)
    },
    [onSelect, defaultValue],
  )
  const currentValue = useMemo(() => ({ value, name: data.find(item => item.value === value)?.name }), [value, data])

  return (
    <div className={b()}>
      <CustomAutoComplete
        {...otherProps}
        options={data}
        value={currentValue}
        getOptionLabel={(currentVal: any) => locale[currentVal.name as LocaleSlotsKeysType] || currentVal.name}
        isOptionEqualToValue={(opt: any, currVal: any) => opt.value === currVal.value}
        renderInput={(params: any) => <TextField {...params} />}
        onChange={(_, newValue: any) => handleChangeSelect(newValue)}
        popupIcon={<ExpandMore />}
        renderOption={(renderOptionsProps, option: any) => (
          <CustomMenuItem {...renderOptionsProps} key={option.name}>
            {option.name}
          </CustomMenuItem>
        )}
        disablePortal
      />
    </div>
  )
}
